/**
 * Runtime matte extraction.
 *
 * A hologram needs to know which pixels of a picture are "the subject". Any
 * image the user drops in — a card render on a grey studio backdrop, a PNG
 * cutout, a render on black — has to become that silhouette in the browser,
 * with no asset pipeline.
 *
 * The work happens ONCE per image URL and the result is shared by every token
 * pointing at it, so a board of 40 tokens costs one pass over one bitmap.
 */

export type MatteMode = 'auto' | 'cutout' | 'luminance' | 'frame' | 'raw'

export interface Matte {
  /** Object URL of the processed RGBA bitmap — feed to background-image + mask-image. */
  url: string
  /**
   * Object URL of the rim: white pixels whose alpha is a band just inside the
   * silhouette. Empty when the subject has no separable outline (`raw`).
   *
   * This is the layer that makes the projection survive being small. Everything
   * else the token draws — the tint ramp, the bloom, the scanlines — is interior
   * fill, and interior fill is low-frequency detail that averages away the moment
   * the token is downsampled to board size. A contour does not: it is the highest-
   * frequency thing in the image and the one the eye uses to recognise a shape at
   * all. It is also what a viewer already reads as "hologram", so it earns its
   * cost twice.
   */
  rimUrl: string
  width: number
  height: number
  /** What the extractor actually decided to do. */
  mode: Exclude<MatteMode, 'auto'>
}

const MAX_EDGE = 512

const cache = new Map<string, Promise<Matte>>()

export function getMatte(src: string, mode: MatteMode = 'auto'): Promise<Matte> {
  const key = `${mode}|${src}`
  let job = cache.get(key)
  if (!job) {
    job = extract(src, mode).catch((err) => {
      cache.delete(key)
      throw err
    })
    cache.set(key, job)
  }
  return job
}

export function clearMatteCache() {
  for (const job of cache.values())
    job
      .then((m) => {
        URL.revokeObjectURL(m.url)
        if (m.rimUrl) URL.revokeObjectURL(m.rimUrl)
      })
      .catch(() => {})
  cache.clear()
}

function loadImage(src: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image()
    // Needed to read pixels back from remote URLs; hosts without CORS headers
    // fail here and the caller falls back to using the image untouched.
    img.crossOrigin = 'anonymous'
    img.onload = () => resolve(img)
    img.onerror = () => reject(new Error(`could not load ${src}`))
    img.src = src
  })
}

async function extract(src: string, mode: MatteMode): Promise<Matte> {
  if (mode === 'raw') {
    const img = await loadImage(src)
    return { url: src, rimUrl: '', width: img.naturalWidth, height: img.naturalHeight, mode: 'raw' }
  }

  const img = await loadImage(src)
  const scale = Math.min(1, MAX_EDGE / Math.max(img.naturalWidth, img.naturalHeight))
  const w = Math.max(1, Math.round(img.naturalWidth * scale))
  const h = Math.max(1, Math.round(img.naturalHeight * scale))

  const canvas = document.createElement('canvas')
  canvas.width = w
  canvas.height = h
  const ctx = canvas.getContext('2d', { willReadFrequently: true })!
  ctx.drawImage(img, 0, 0, w, h)

  let data: ImageData
  try {
    data = ctx.getImageData(0, 0, w, h)
  } catch {
    // Tainted canvas (remote image, no CORS). Use the picture as-is.
    return { url: src, rimUrl: '', width: img.naturalWidth, height: img.naturalHeight, mode: 'raw' }
  }

  // Computed once and shared: the fill, the cleanup and the strategy choice
  // all reason about the same blurred copy and the same edge map.
  const smooth = blurRGB(data)
  const edges = sobel(smooth, w, h)

  let chosen = mode === 'auto' ? decide(data, edges) : mode
  if (chosen === 'cutout' && !cutoutMatte(data, smooth, edges) && mode === 'auto') {
    // The fill ate the subject — the picture has no separable backdrop.
    ctx.drawImage(img, 0, 0, w, h)
    data = ctx.getImageData(0, 0, w, h)
    chosen = 'frame'
  }
  if (chosen === 'luminance') luminanceMatte(data)
  else if (chosen === 'frame') frameMatte(data)
  if (chosen !== 'frame') fillHoles(data)
  featherAlpha(data)
  despeckle(data.data, w, h)
  normaliseContrast(data)
  liftColour(data)
  floorLuma(data)

  const cropped = cropToSubject(data)
  const core = bodyMask(cropped)
  attenuateFiligree(cropped, core)

  canvas.width = cropped.width
  canvas.height = cropped.height
  ctx.putImageData(cropped, 0, 0)

  const blob = await new Promise<Blob | null>((res) => canvas.toBlob(res, 'image/png'))
  if (!blob) return { url: src, rimUrl: '', width: w, height: h, mode: 'raw' }

  // The rim comes off the SAME cropped alpha the figure is drawn from, so the two
  // bitmaps share an origin and a scale and the contour cannot creep away from the
  // body it belongs to.
  ctx.putImageData(buildRim(cropped, core), 0, 0)
  const rimBlob = await new Promise<Blob | null>((res) => canvas.toBlob(res, 'image/png'))

  return {
    url: URL.createObjectURL(blob),
    rimUrl: rimBlob ? URL.createObjectURL(rimBlob) : '',
    width: cropped.width,
    height: cropped.height,
    mode: chosen,
  }
}

/**
 * The contour, as a band lying just INSIDE the silhouette.
 *
 * Inside rather than straddling the edge, because the token masks the figure by
 * this same alpha: half of a straddling band would be clipped away, and the half
 * that survived would be the ragged outer half. An inner band is also the more
 * truthful shape — light gathers where a projected surface turns away from the
 * viewer, which happens short of the outline, not past it.
 *
 * `alpha - erode(alpha)` is the whole trick. Erosion by r pulls the silhouette in
 * by r; subtracting leaves exactly the r-wide skin that was removed. Anti-aliased
 * edge pixels come out proportionally weaker, which feathers the band for free.
 */
/** Band width used by both the body mask and the rim, as a fraction of the bitmap,
 *  so both are the same share of the figure whatever resolution the art arrived at. */
function rimRadius(w: number, h: number): number {
  return Math.max(1, Math.round(Math.min(w, h) * 0.022))
}

function alphaPlane(d: ImageData): Uint8ClampedArray {
  const px = d.width * d.height
  const out = new Uint8ClampedArray(px)
  for (let i = 0, p = 0; p < px; i += 4, p++) out[p] = d.data[i + 3]
  return out
}

/**
 * Where the subject has real mass, as opposed to filigree.
 *
 * An erosion followed by a dilation is an opening: it answers "is there a form at
 * least this thick here?" — broad shapes survive it more or less unchanged, while
 * anything thinner than the radius is erased by the erosion and never comes back.
 * That is exactly the distinction a character needs, because a character is not all
 * body. Flowing hair, a cloak hem, a wisp of smoke are all narrower than the band.
 */
function bodyMask(d: ImageData): Uint8ClampedArray {
  const { width: w, height: h } = d
  const r = rimRadius(w, h)
  return dilateMax(erodeMin(alphaPlane(d), w, h, r), w, h, Math.round(r * 1.7))
}

/**
 * Let solid forms project strongly and fine detail project faintly.
 *
 * Without this a character whose art is mostly hair — and one of ours is — arrives on
 * the board as a flame. Every strand carries the full tint, there are far more strands
 * than there is person, and at sixty pixels the mass of filigree simply outvotes the
 * body: the token reads as a burning blob with a face somewhere in it.
 *
 * Weighting by the opening fixes the vote rather than the strands. It is also the
 * physically honest reading — a thin, wispy surface scatters less light back than a
 * broad one — so the hair stays present, and stays hair, instead of being cut off with
 * a threshold that would leave the character shorn.
 */
function attenuateFiligree(d: ImageData, core: Uint8ClampedArray, keep = 0.34) {
  const { data } = d
  const span = 1 - keep
  for (let p = 0, i = 3; p < core.length; p++, i += 4) {
    data[i] = data[i] * (keep + span * (core[p] / 255))
  }
}

function buildRim(d: ImageData, core: Uint8ClampedArray): ImageData {
  const { width: w, height: h } = d
  const px = w * h

  const alpha = alphaPlane(d)
  const eroded = erodeMin(alpha, w, h, rimRadius(w, h))

  const out = new ImageData(w, h)
  for (let p = 0, i = 0; p < px; p++, i += 4) {
    const band = alpha[p] - eroded[p]
    out.data[i] = 255
    out.data[i + 1] = 255
    out.data[i + 2] = 255
    // Squared so the band peaks hard and falls off fast; a linear ramp reads as a
    // soft glow round the figure rather than as an edge. Gated on the body mask for
    // the same reason it exists: a rim light is a property of a surface broad enough
    // to turn away from you, and tracing every strand of hair is neon, not lighting.
    out.data[i + 3] = Math.round(((band * band) / 255) * (core[p] / 255))
  }
  return out
}

/** Separable min filter — erosion by a (2r+1) square. */
function erodeMin(a: Uint8ClampedArray, w: number, h: number, r: number): Uint8ClampedArray {
  return rank(a, w, h, r, false)
}

/** Separable max filter — dilation by a (2r+1) square. */
function dilateMax(a: Uint8ClampedArray, w: number, h: number, r: number): Uint8ClampedArray {
  return rank(a, w, h, r, true)
}

function rank(
  a: Uint8ClampedArray,
  w: number,
  h: number,
  r: number,
  max: boolean,
): Uint8ClampedArray {
  const seed = max ? 0 : 255
  const better = max
    ? (v: number, m: number) => v > m
    : (v: number, m: number) => v < m

  const tmp = new Uint8ClampedArray(w * h)
  const out = new Uint8ClampedArray(w * h)

  for (let y = 0; y < h; y++) {
    const row = y * w
    for (let x = 0; x < w; x++) {
      let m = seed
      const x0 = Math.max(0, x - r)
      const x1 = Math.min(w - 1, x + r)
      for (let k = x0; k <= x1; k++) if (better(a[row + k], m)) m = a[row + k]
      tmp[row + x] = m
    }
  }
  for (let x = 0; x < w; x++) {
    for (let y = 0; y < h; y++) {
      let m = seed
      const y0 = Math.max(0, y - r)
      const y1 = Math.min(h - 1, y + r)
      for (let k = y0; k <= y1; k++) if (better(tmp[k * w + x], m)) m = tmp[k * w + x]
      out[y * w + x] = m
    }
  }
  return out
}

/**
 * Lift the black point of the subject so nothing in it projects as *nothing*.
 *
 * The tint ramp reads its value from the artwork's luminance, so a pixel at zero
 * comes out at zero — and a black hood or a black dress is then not a dark part of
 * the figure, it is a hole with the map showing through. That is precisely how a
 * robed character ends up reading as a floating head and a pair of hands.
 *
 * `liftColour` above cannot fix this: it solves a gamma against the MEDIAN, which
 * moves the midtones and leaves true black exactly where it was. A floor is a
 * separate, affine move — blacks up, whites fixed — and it is what makes the
 * garment read as dark-but-emitting, which is what a projection of a dark surface
 * actually looks like.
 */
function floorLuma(d: ImageData, floor = 0.17) {
  const { data } = d
  const lift = floor * 255
  const span = 1 - floor
  for (let i = 0; i < data.length; i += 4) {
    if (data[i + 3] === 0) continue
    data[i] = lift + data[i] * span
    data[i + 1] = lift + data[i + 1] * span
    data[i + 2] = lift + data[i + 2] * span
  }
}

/**
 * Pick a strategy by asking what kind of picture this is.
 *
 * The deciding question is whether a backdrop exists at all, and the honest
 * test for that is texture, not colour. A studio backdrop is SMOOTH: a sweep
 * or a vignette, near-zero gradient everywhere along the frame. A full-bleed
 * illustration has detail running right off the edge — fire, sparks, rock,
 * fur. Colour thresholds cannot tell those apart, because a dark scene and a
 * dark backdrop occupy the same range and a vignette drifts as far as a
 * subject does; edge energy separates them cleanly.
 *
 *   transparent border  -> the artist gave us a cutout; keep their alpha
 *   smooth border       -> a backdrop; flood it away
 *   busy border, and
 *     a brighter centre  -> a lit render on a dark field; brightness is the
 *                           matte, which is what a projector would show
 *   busy border, and
 *     no brighter centre -> nothing can be isolated. Project the frame and let
 *                           the beam cone and feather masks shape it. A soft
 *                           cone of artwork reads as a hologram; the confetti
 *                           a failed cutout leaves behind does not.
 */
function decide(d: ImageData, edges: Uint8ClampedArray): Exclude<MatteMode, 'auto' | 'raw'> {
  const { data, width: w, height: h } = d
  const borderLum: number[] = []
  let transparent = 0
  let edgeSum = 0
  let n = 0

  // Sampled one pixel in from the frame: the outermost row has no Sobel.
  const sample = (x: number, y: number) => {
    const i = (y * w + x) * 4
    if (data[i + 3] < 24) transparent++
    borderLum.push(luma(data[i], data[i + 1], data[i + 2]))
    edgeSum += edges[y * w + x]
    n++
  }
  for (let x = 1; x < w - 1; x += 2) {
    sample(x, 1)
    sample(x, h - 2)
  }
  for (let y = 1; y < h - 1; y += 2) {
    sample(1, y)
    sample(w - 2, y)
  }

  if (transparent / n > 0.6) return 'cutout' // already alpha; the fill is a no-op

  // Measured across the test corpus: a studio sweep sits near 1, a lit render
  // on a noisy black field near 6.5, a full-bleed illustration near 4.7. The
  // gap between "smooth enough to flood" and everything else is wide.
  if (edgeSum / n <= 3) return 'cutout'

  const centreLum: number[] = []
  for (let y = Math.floor(h * 0.2); y < h * 0.8; y += 3) {
    for (let x = Math.floor(w * 0.25); x < w * 0.75; x += 3) {
      const i = (y * w + x) * 4
      centreLum.push(luma(data[i], data[i + 1], data[i + 2]))
    }
  }

  return median(centreLum) > median(borderLum) + 0.05 ? 'luminance' : 'frame'
}

function median(a: number[]): number {
  if (!a.length) return 0
  const s = [...a].sort((p, q) => p - q)
  return s[s.length >> 1]
}

/**
 * No isolation possible: keep the whole picture, but round its corners off
 * with an elliptical falloff so the projection never shows a hard rectangle.
 * The beam cone and the bottom dissolve do the rest of the shaping in CSS.
 *
 * The radii are the distances from the centre to each edge, not a fixed
 * fraction of the bitmap, because the falloff only reaches zero at r = 1: with
 * a radius wider than the half-width the fade is still half-open when the
 * bitmap ends, and the projection is cut off mid-gradient - a vertical seam
 * down both sides and a brighter one across the top, exactly the hard rectangle
 * this is here to prevent. Measured against the offending art the old radii
 * left alpha 122/255 on the side edges and 217/255 on the top.
 *
 * The centre sits above the middle, so above and below get their own radius;
 * sharing one would push the fade past whichever edge is nearer.
 */
function frameMatte(d: ImageData) {
  const { data, width: w, height: h } = d
  const cx = w / 2
  const cy = h * 0.46
  const rx = w / 2
  const ryAbove = cy
  const ryBelow = h - cy

  for (let y = 0; y < h; y++) {
    for (let x = 0; x < w; x++) {
      const dx = (x - cx) / rx
      const dy = (y - cy) / (y < cy ? ryAbove : ryBelow)
      const r = Math.sqrt(dx * dx + dy * dy)
      const a = r <= 0.72 ? 1 : r >= 1 ? 0 : 1 - (r - 0.72) / 0.28
      const i = (y * w + x) * 4
      data[i + 3] = Math.round(data[i + 3] * a * a)
    }
  }
}

/**
 * Close transparency that is INSIDE the subject.
 *
 * Both extractors punch holes for the same reason: they decide per-pixel whether
 * something is backdrop, and a dark robe, a black hood or a shadowed flank answers
 * that question the same way an actual backdrop does. The flood fill walks into the
 * garment through a shadowed seam; the luminance matte simply reads it as unlit. The
 * result is a subject rendered as lace — and lace is not a subtle defect, because the
 * map shows through every gap and a hooded character stops being a person at all.
 *
 * Connectivity settles it where colour cannot. Real backdrop touches the frame; a
 * garment does not, however dark it is. So transparency is flooded from the border,
 * and anything transparent the flood never reaches is interior by definition and
 * gets its opacity back. The pixels are still there underneath — only the alpha was
 * ever wrong — so restoring it returns the actual robe rather than a patch.
 *
 * Skipped for `frame`, whose alpha is a deliberate elliptical falloff: every
 * transparent pixel there is meant to be transparent, and its soft interior would
 * read as unreachable and be filled solid.
 */
function fillHoles(d: ImageData) {
  const { data, width: w, height: h } = d
  const px = w * h
  const CLEAR = 12

  const outside = new Uint8Array(px)
  const stack = new Int32Array(px)
  let top = 0

  const push = (idx: number) => {
    if (!outside[idx] && data[idx * 4 + 3] < CLEAR) {
      outside[idx] = 1
      stack[top++] = idx
    }
  }
  for (let x = 0; x < w; x++) {
    push(x)
    push((h - 1) * w + x)
  }
  for (let y = 0; y < h; y++) {
    push(y * w)
    push(y * w + w - 1)
  }

  while (top > 0) {
    const idx = stack[--top]
    const x = idx % w
    const y = (idx - x) / w
    if (x > 0) push(idx - 1)
    if (x < w - 1) push(idx + 1)
    if (y > 0) push(idx - w)
    if (y < h - 1) push(idx + w)
  }

  // Only pixels that are actually clear get closed. A partially transparent pixel is
  // carrying information — an anti-aliased edge, or, under the luminance matte, the
  // subject's own shading — and promoting those to solid would trade the lace for a
  // hard-edged sticker.
  for (let idx = 0; idx < px; idx++) {
    if (!outside[idx] && data[idx * 4 + 3] < CLEAR) data[idx * 4 + 3] = 255
  }
}

/**
 * Drop specks. Whatever the strategy, a little of the scene always survives as
 * dust: a rim-lit leaf, a spark, a scrap of backdrop the fill could not reach.
 * Individually invisible, collectively they wreck the framing — the subject's
 * bounding box becomes the whole frame, and the projection ends up floating
 * small and off-centre above its own beam.
 */
function despeckle(data: Uint8ClampedArray, w: number, h: number) {
  const px = w * h
  const minSize = Math.max(24, Math.round(px * 0.0016))
  const seen = new Uint8Array(px)
  const stack = new Int32Array(px)
  const blob = new Int32Array(px)

  for (let start = 0; start < px; start++) {
    if (seen[start] || data[start * 4 + 3] < 12) continue
    let top = 0
    let n = 0
    seen[start] = 1
    stack[top++] = start

    while (top > 0) {
      const idx = stack[--top]
      blob[n++] = idx
      const x = idx % w
      const y = (idx - x) / w
      const push = (next: number) => {
        if (!seen[next] && data[next * 4 + 3] >= 12) {
          seen[next] = 1
          stack[top++] = next
        }
      }
      if (x > 0) push(idx - 1)
      if (x < w - 1) push(idx + 1)
      if (y > 0) push(idx - w)
      if (y < h - 1) push(idx + w)
    }

    if (n < minSize) for (let k = 0; k < n; k++) data[blob[k] * 4 + 3] = 0
  }
}

/**
 * Trim to what actually survived. Source art frames its subject anywhere it
 * likes — centred, top-heavy, floating in a wide canvas — and the token places
 * the projection by fitting the image box, not the subject. Without this the
 * figure hangs in the air above its own beam whenever the art has padding.
 */
function cropToSubject(d: ImageData): ImageData {
  const { data, width: w, height: h } = d
  let x0 = w
  let y0 = h
  let x1 = -1
  let y1 = -1

  for (let y = 0; y < h; y++) {
    for (let x = 0; x < w; x++) {
      if (data[(y * w + x) * 4 + 3] < 12) continue
      if (x < x0) x0 = x
      if (x > x1) x1 = x
      if (y < y0) y0 = y
      if (y > y1) y1 = y
    }
  }
  if (x1 < 0) return d

  const pad = 2
  x0 = Math.max(0, x0 - pad)
  y0 = Math.max(0, y0 - pad)
  x1 = Math.min(w - 1, x1 + pad)
  y1 = Math.min(h - 1, y1 + pad)
  const cw = x1 - x0 + 1
  const ch = y1 - y0 + 1
  if (cw === w && ch === h) return d

  const out = new ImageData(cw, ch)
  for (let y = 0; y < ch; y++) {
    const from = ((y + y0) * w + x0) * 4
    out.data.set(data.subarray(from, from + cw * 4), y * cw * 4)
  }
  return out
}

function luma(r: number, g: number, b: number): number {
  return (0.2126 * r + 0.7152 * g + 0.0722 * b) / 255
}

/** 80th-percentile brightness of the border — the brightest the backdrop gets. */
function borderLuma(d: ImageData): number {
  const { data, width: w, height: h } = d
  const vals: number[] = []
  const take = (x: number, y: number) => {
    const i = (y * w + x) * 4
    vals.push(luma(data[i], data[i + 1], data[i + 2]))
  }
  for (let x = 0; x < w; x += 2) {
    take(x, 0)
    take(x, h - 1)
  }
  for (let y = 0; y < h; y += 2) {
    take(0, y)
    take(w - 1, y)
  }
  vals.sort((a, b) => a - b)
  return vals[Math.floor(vals.length * 0.8)] ?? 0
}

/**
 * Brightness becomes opacity, with the backdrop crushed to nothing. The black
 * point is measured from the border rather than fixed, because "on black" in
 * practice means anything from #000 to a lifted charcoal vignette — and a black
 * point even slightly under the real one leaves a visible rectangle of haze
 * around the projection.
 */
function luminanceMatte(d: ImageData) {
  const { data } = d
  const black = Math.min(0.4, borderLuma(d) + 0.06)
  const scale = 1 / (1 - black)
  for (let i = 0; i < data.length; i += 4) {
    const l = (luma(data[i], data[i + 1], data[i + 2]) - black) * scale
    const a = l <= 0 ? 0 : Math.min(1, Math.pow(l, 0.72))
    data[i + 3] = Math.round(a * data[i + 3])
  }
}

/**
 * Flood the backdrop away, starting from every border pixel.
 *
 * Colour distance alone cannot do this job. A studio vignette drifts ~60 levels
 * from centre to corner, so any tolerance loose enough to follow it is also
 * loose enough to walk into a black hoodie — and the corners of a vignette are
 * *the same brightness* as that hoodie, so there is no threshold that separates
 * them. What does separate them is the edge: the vignette changes about a level
 * per pixel, the silhouette tens of levels over two or three.
 *
 * So the fill is blocked by gradient magnitude, not by colour: it flows freely
 * across anything smooth and stops dead at any real boundary. The subject's own
 * interior is smooth too, but it is fenced in by its outline, so the fill never
 * reaches it. Colour distance survives only as a loose sanity bound.
 *
 * Decisions are read from a blurred copy so film grain cannot stall the fill;
 * alpha is written to the original pixels.
 *
 * Returns false if the fill escaped anyway.
 */
function cutoutMatte(
  d: ImageData,
  smooth: Uint8ClampedArray,
  edges: Uint8ClampedArray,
  edgeTol = 6,
  stepPerChannel = 14,
): boolean {
  const { data, width: w, height: h } = d
  const px = w * h

  let opaque = 0
  for (let i = 3; i < data.length; i += 4) if (data[i] > 24) opaque++
  if (opaque / px < 0.98) return true // genuine alpha channel already present

  const ref = medianBorderColour(d)
  const stepTol = stepPerChannel * stepPerChannel * 3
  const globalTol = 130 * 130 * 3

  const seen = new Uint8Array(px)
  const stack = new Int32Array(px)
  let top = 0

  const push = (idx: number) => {
    if (!seen[idx]) {
      seen[idx] = 1
      stack[top++] = idx
    }
  }
  for (let x = 0; x < w; x++) {
    push(x)
    push((h - 1) * w + x)
  }
  for (let y = 0; y < h; y++) {
    push(y * w)
    push(y * w + w - 1)
  }

  const dist = (a: number, b: number) => {
    const dr = smooth[a] - smooth[b]
    const dg = smooth[a + 1] - smooth[b + 1]
    const db = smooth[a + 2] - smooth[b + 2]
    return dr * dr + dg * dg + db * db
  }
  const distRef = (a: number) => {
    const dr = smooth[a] - ref[0]
    const dg = smooth[a + 1] - ref[1]
    const db = smooth[a + 2] - ref[2]
    return dr * dr + dg * dg + db * db
  }

  const bg = new Uint8Array(px)
  const neighbours = [0, 0, 0, 0]
  let removed = 0

  while (top > 0) {
    const idx = stack[--top]
    const i = idx * 3
    if (distRef(i) > globalTol) continue
    bg[idx] = 1
    removed++

    const x = idx % w
    const y = (idx - x) / w
    let n = 0
    if (x > 0) neighbours[n++] = idx - 1
    if (x < w - 1) neighbours[n++] = idx + 1
    if (y > 0) neighbours[n++] = idx - w
    if (y < h - 1) neighbours[n++] = idx + w

    for (let k = 0; k < n; k++) {
      const next = neighbours[k]
      if (seen[next] || edges[next] > edgeTol || dist(next * 3, i) > stepTol) continue
      seen[next] = 1
      stack[top++] = next
    }
  }

  // A backdrop is a backdrop; if "background" swallowed the frame, it leaked.
  if (removed / px > 0.88) {
    return edgeTol > 3 ? cutoutMatte(d, smooth, edges, 3, 8) : false
  }

  growBackground(bg, smooth, edges, w, h, edgeTol)

  // A cutout is one solid object. Two ways it can fail to be one, both of which
  // happen when the "backdrop" was really a painted scene and the fill wandered
  // through the subject's own dark areas: it comes back as disconnected specks,
  // or as lace — connected, but mostly holes. Area alone catches neither, since
  // either can leave plenty of pixels behind.
  if (
    largestComponentShare(bg, data, w, h) < 0.55 ||
    subjectDensity(bg, data, w, h) < 0.3
  ) {
    return edgeTol > 3 ? cutoutMatte(d, smooth, edges, 3, 8) : false
  }

  for (let idx = 0; idx < px; idx++) if (bg[idx]) data[idx * 4 + 3] = 0
  return true
}

/** How much of its own bounding box the surviving subject actually fills. */
function subjectDensity(bg: Uint8Array, data: Uint8ClampedArray, w: number, h: number): number {
  let x0 = w
  let y0 = h
  let x1 = -1
  let y1 = -1
  let kept = 0

  for (let y = 0; y < h; y++) {
    for (let x = 0; x < w; x++) {
      const idx = y * w + x
      if (bg[idx] || data[idx * 4 + 3] < 12) continue
      kept++
      if (x < x0) x0 = x
      if (x > x1) x1 = x
      if (y < y0) y0 = y
      if (y > y1) y1 = y
    }
  }
  if (x1 < 0) return 0
  return kept / ((x1 - x0 + 1) * (y1 - y0 + 1))
}

/** Share of the surviving subject that sits in its single largest blob. */
function largestComponentShare(
  bg: Uint8Array,
  data: Uint8ClampedArray,
  w: number,
  h: number,
): number {
  const px = w * h
  const keep = new Uint8Array(px)
  let total = 0
  for (let idx = 0; idx < px; idx++) {
    if (!bg[idx] && data[idx * 4 + 3] >= 12) {
      keep[idx] = 1
      total++
    }
  }
  if (!total) return 0

  const seen = new Uint8Array(px)
  const stack = new Int32Array(px)
  let largest = 0

  for (let start = 0; start < px; start++) {
    if (!keep[start] || seen[start]) continue
    let top = 0
    let size = 0
    seen[start] = 1
    stack[top++] = start

    while (top > 0) {
      const idx = stack[--top]
      size++
      const x = idx % w
      const y = (idx - x) / w
      if (x > 0 && keep[idx - 1] && !seen[idx - 1]) (seen[idx - 1] = 1), (stack[top++] = idx - 1)
      if (x < w - 1 && keep[idx + 1] && !seen[idx + 1]) (seen[idx + 1] = 1), (stack[top++] = idx + 1)
      if (y > 0 && keep[idx - w] && !seen[idx - w]) (seen[idx - w] = 1), (stack[top++] = idx - w)
      if (y < h - 1 && keep[idx + w] && !seen[idx + w]) (seen[idx + w] = 1), (stack[top++] = idx + w)
    }
    if (size > largest) largest = size
  }

  return largest / total
}

/**
 * Mop up backdrop the fill stalled against — a compression streak or a noisy
 * band reads as an edge and leaves a sliver of frame behind.
 *
 * The test is against the ADJOINING background pixel, not the backdrop's
 * average: a stalled band continues the colour of what it touches almost
 * exactly, while a dark costume sits a good 30 levels below it. Measuring
 * against the average instead lets any subject that happens to be within
 * tolerance of the backdrop get eaten from the outside in.
 */
function growBackground(
  bg: Uint8Array,
  smooth: Uint8ClampedArray,
  edges: Uint8ClampedArray,
  w: number,
  h: number,
  edgeTol: number,
) {
  const tol = 11 * 11 * 3

  for (let pass = 0; pass < 3; pass++) {
    const added: number[] = []

    for (let y = 0; y < h; y++) {
      for (let x = 0; x < w; x++) {
        const idx = y * w + x
        if (bg[idx] || edges[idx] > edgeTol) continue

        const i = idx * 3
        let matched = false
        for (let k = 0; k < 4 && !matched; k++) {
          const nx = x + (k === 0 ? -1 : k === 1 ? 1 : 0)
          const ny = y + (k === 2 ? -1 : k === 3 ? 1 : 0)
          if (nx < 0 || ny < 0 || nx >= w || ny >= h) continue
          const n = ny * w + nx
          if (!bg[n]) continue
          const j = n * 3
          const dr = smooth[i] - smooth[j]
          const dg = smooth[i + 1] - smooth[j + 1]
          const db = smooth[i + 2] - smooth[j + 2]
          matched = dr * dr + dg * dg + db * db < tol
        }
        if (matched) added.push(idx)
      }
    }

    if (!added.length) return
    for (const idx of added) bg[idx] = 1
  }
}

/** Sobel magnitude over the blurred luma, clamped to a byte. */
function sobel(rgb: Uint8ClampedArray, w: number, h: number): Uint8ClampedArray {
  const lum = new Float32Array(w * h)
  for (let p = 0, i = 0; p < lum.length; p++, i += 3) {
    lum[p] = 0.2126 * rgb[i] + 0.7152 * rgb[i + 1] + 0.0722 * rgb[i + 2]
  }

  const out = new Uint8ClampedArray(w * h)
  for (let y = 1; y < h - 1; y++) {
    for (let x = 1; x < w - 1; x++) {
      const p = y * w + x
      const gx =
        -lum[p - w - 1] + lum[p - w + 1] +
        -2 * lum[p - 1] + 2 * lum[p + 1] +
        -lum[p + w - 1] + lum[p + w + 1]
      const gy =
        -lum[p - w - 1] - 2 * lum[p - w] - lum[p - w + 1] +
        lum[p + w - 1] + 2 * lum[p + w] + lum[p + w + 1]
      out[p] = Math.sqrt(gx * gx + gy * gy) / 4
    }
  }
  return out
}

/** 3x3 box blur of the RGB planes, packed tight — used only for fill decisions. */
function blurRGB(d: ImageData): Uint8ClampedArray {
  const { data, width: w, height: h } = d
  const out = new Uint8ClampedArray(w * h * 3)
  for (let y = 0; y < h; y++) {
    for (let x = 0; x < w; x++) {
      const x0 = x > 0 ? x - 1 : x
      const x1 = x < w - 1 ? x + 1 : x
      const y0 = y > 0 ? y - 1 : y
      const y1 = y < h - 1 ? y + 1 : y
      let r = 0
      let g = 0
      let b = 0
      let n = 0
      for (let yy = y0; yy <= y1; yy++) {
        for (let xx = x0; xx <= x1; xx++) {
          const i = (yy * w + xx) * 4
          r += data[i]
          g += data[i + 1]
          b += data[i + 2]
          n++
        }
      }
      const o = (y * w + x) * 3
      out[o] = r / n
      out[o + 1] = g / n
      out[o + 2] = b / n
    }
  }
  return out
}

function medianBorderColour(d: ImageData): [number, number, number] {
  const { data, width: w, height: h } = d
  const rs: number[] = []
  const gs: number[] = []
  const bs: number[] = []
  const take = (x: number, y: number) => {
    const i = (y * w + x) * 4
    rs.push(data[i])
    gs.push(data[i + 1])
    bs.push(data[i + 2])
  }
  for (let x = 0; x < w; x += 3) {
    take(x, 0)
    take(x, h - 1)
  }
  for (let y = 0; y < h; y += 3) {
    take(0, y)
    take(w - 1, y)
  }
  const mid = (a: number[]) => a.sort((p, q) => p - q)[a.length >> 1]
  return [mid(rs), mid(gs), mid(bs)]
}

/** 3x3 box blur on alpha only — kills the jagged edge the flood fill leaves. */
function featherAlpha(d: ImageData) {
  const { data, width: w, height: h } = d
  const src = new Uint8Array(w * h)
  for (let i = 0, p = 0; i < data.length; i += 4, p++) src[p] = data[i + 3]

  for (let y = 1; y < h - 1; y++) {
    for (let x = 1; x < w - 1; x++) {
      const p = y * w + x
      const sum =
        src[p - w - 1] + src[p - w] + src[p - w + 1] +
        src[p - 1] + src[p] + src[p + 1] +
        src[p + w - 1] + src[p + w] + src[p + w + 1]
      data[p * 4 + 3] = (sum / 9) | 0
    }
  }
}

/**
 * Spend the whole tonal range on every subject.
 *
 * The tint ramp turns luminance into the only channel the projection has: hue is
 * fixed by the token's colour, so whatever separation the art had between a face, a
 * hood and a cloak has to survive as brightness or it does not survive at all. Art
 * that was painted low-contrast — a dark figure lit by one dim source, which is most
 * of a moody character render — arrives using perhaps a third of the range, and a
 * third of the range through a single-hue ramp is one flat mass of colour with a
 * suggestion of a face in it.
 *
 * Stretching to percentiles rather than to the true min and max is what makes this
 * safe: a single specular highlight or one crushed shadow pixel would otherwise set
 * the endpoints and the stretch would do nothing at all.
 *
 * This has to run BEFORE the gamma lift and the floor. Both of those are corrections
 * applied to a known range — one solves for the median, the other raises the black
 * point — and neither means anything if the range itself is still arbitrary.
 */
function normaliseContrast(d: ImageData) {
  const { data } = d

  const samples: number[] = []
  for (let i = 0; i < data.length; i += 4 * 5) {
    if (data[i + 3] < 40) continue
    samples.push(luma(data[i], data[i + 1], data[i + 2]))
  }
  if (samples.length < 32) return

  samples.sort((a, b) => a - b)
  const lo = samples[Math.floor(samples.length * 0.02)]
  const hi = samples[Math.floor(samples.length * 0.98)]
  if (!(hi - lo > 0.02) || hi - lo > 0.92) return

  const scale = 255 / ((hi - lo) * 255)
  const black = lo * 255
  const lut = new Uint8Array(256)
  for (let v = 0; v < 256; v++) {
    lut[v] = Math.max(0, Math.min(255, Math.round((v - black) * scale)))
  }
  for (let i = 0; i < data.length; i += 4) {
    if (data[i + 3] === 0) continue
    data[i] = lut[data[i]]
    data[i + 1] = lut[data[i + 1]]
    data[i + 2] = lut[data[i + 2]]
  }
}

/**
 * Normalise exposure so every picture projects at the same strength.
 *
 * The tint ramp takes its luminance from the artwork, so a black-costumed
 * character comes out as a silhouette-shaped hole while a bright one blows
 * out — and on a board those two sit side by side. Rather than a fixed curve,
 * the gamma is solved from the subject's own median so it lands on mid grey:
 * dark art is lifted hard, already-bright art is left alone.
 */
function liftColour(d: ImageData) {
  const { data } = d

  const samples: number[] = []
  for (let i = 0; i < data.length; i += 4 * 7) {
    if (data[i + 3] < 40) continue
    samples.push(luma(data[i], data[i + 1], data[i + 2]))
  }
  const mid = median(samples)

  const target = 0.46
  let gamma = 1
  if (mid > 0.004 && mid < 0.995) {
    gamma = Math.log(target) / Math.log(mid)
    gamma = Math.max(0.34, Math.min(1.15, gamma))
  }

  const lut = new Uint8Array(256)
  for (let v = 0; v < 256; v++) {
    lut[v] = Math.min(255, Math.round(255 * Math.pow(v / 255, gamma)))
  }
  for (let i = 0; i < data.length; i += 4) {
    if (data[i + 3] === 0) continue
    data[i] = lut[data[i]]
    data[i + 1] = lut[data[i + 1]]
    data[i + 2] = lut[data[i + 2]]
  }
}
